// src/features/messages/MessageBubble.tsx — a single chat message bubble.
import styled from 'styled-components';
import { Icon } from '@/icons';
import { useMediaViewer } from '@/components/MediaViewer';
import { mediaLabel } from './meta';
import type { Message } from '@/core/models/message';

const Row = styled.div<{ $me: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $me }) => ($me ? 'flex-end' : 'flex-start')};
  gap: 2px;
`;
const Author = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  padding: 0 4px;
`;
const Bubble = styled.div<{ $me: boolean }>`
  max-width: 78%;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme, $me }) => ($me ? theme.color.onAccent : theme.color.textPrimary)};
  background: ${({ theme, $me }) => ($me ? theme.color.accentCyan : theme.color.glassBg)};
  border: 1px solid ${({ theme, $me }) => ($me ? 'transparent' : theme.color.glassBorder)};
`;
const Time = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  padding: 0 4px;
`;
const Seen = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  padding: 0 4px;
`;
const ImageBlock = styled.div`
  width: 180px; height: 120px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.accentCyan};
  display: grid; place-items: center; gap: 6px;
  grid-auto-flow: row;
`;
const ImageLabel = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.color.accentCyan};
`;
const Voice = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
`;
// Real attachment (1.24.00). Capped at 240×240 so a portrait photo can't push
// the whole thread off-screen; the full-size version lives in the MediaViewer.
const AttachmentButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;
const AttachmentImg = styled.img`
  display: block;
  max-width: 240px;
  max-height: 240px;
  width: auto;
  height: auto;
  border-radius: ${({ theme }) => theme.radius.sm};
`;
const Caption = styled.p`
  margin: 6px 0 0;
`;

export interface MessageBubbleProps {
  message: Message;
  isGroup: boolean;
  /**
   * 1.24.00 read receipts — the other participant's last read instant, or null
   * when EITHER side has receipts off. Only consulted on `showReceipt` bubbles.
   */
  otherReadAt?: string | null;
  /**
   * True only for the LAST message I sent in the thread — one "Seen" line per
   * conversation, not one per bubble.
   */
  showReceipt?: boolean;
}

export function MessageBubble({
  message,
  isGroup,
  otherReadAt = null,
  showReceipt = false,
}: MessageBubbleProps) {
  const viewer = useMediaViewer();
  const showAuthor = isGroup && !message.fromMe && message.author;
  const image = message.image ?? null;
  const imageAlt = message.body.trim() !== '' ? message.body : mediaLabel('image');
  // ISO-8601 UTC strings sort lexicographically, so a plain >= is a valid
  // instant comparison and needs no Date parsing. `otherReadAt` is already null
  // whenever either party has read receipts off, so there is nothing more to
  // gate on here — no receipts pref means no "Seen", server-side.
  const seen =
    showReceipt &&
    message.fromMe &&
    otherReadAt !== null &&
    otherReadAt !== undefined &&
    message.createdAt !== undefined &&
    otherReadAt >= message.createdAt;

  return (
    <Row $me={message.fromMe}>
      {showAuthor && <Author>{message.author}</Author>}
      <Bubble $me={message.fromMe}>
        {image ? (
          <>
            <AttachmentButton
              type="button"
              aria-label={`Open photo${message.author ? ` from ${message.author}` : ''}`}
              onClick={() =>
                viewer.open(
                  [{ url: image.url, alt: imageAlt, width: image.width, height: image.height }],
                  0,
                )
              }
            >
              <AttachmentImg
                src={image.url}
                alt={imageAlt}
                {...(image.width ? { width: image.width } : {})}
                {...(image.height ? { height: image.height } : {})}
              />
            </AttachmentButton>
            {message.body !== '' && <Caption>{message.body}</Caption>}
          </>
        ) : message.media === 'image' ? (
          <ImageBlock role="img" aria-label={mediaLabel('image')}>
            <Icon name="image" size={20} />
            <ImageLabel>{mediaLabel('image')}</ImageLabel>
          </ImageBlock>
        ) : message.media === 'voice' ? (
          <Voice>
            <Icon name="mic" size={16} />
            <span>{mediaLabel('voice')}</span>
            <span>{message.body}</span>
          </Voice>
        ) : (
          message.body
        )}
      </Bubble>
      <Time>{message.time}</Time>
      {seen && <Seen>Seen</Seen>}
    </Row>
  );
}
